import React, { useEffect, useEffectEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@/auth/UserContext';
import { EmptyState } from '@/components/EmptyState';
import { useNews } from '@/hooks/useNews';
import { useResetUnseenEvents } from '@/hooks/useResetUnseenEvents';
import { useUnseenEvents } from '@/hooks/useUnseenEvents';
import { NewsCard } from '@/pages/Home/NewsCard';
import { Anchor } from '@/types/anchors';
import { UnseenEventType } from '@/types/components';
import { NewsType } from '@/types/news';
import { SectionTitle } from '@/pages/Home/SectionTitle';

const FULLY_VISIBLE_RATIO = 0.99;

export const News: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const { data: news } = useNews();
  const { data: unseenEvents } = useUnseenEvents();
  const resetUnseenEvents = useResetUnseenEvents();
  const newsGridRef = useRef<HTMLDivElement>(null);

  const isCommercialNewsSubscriber =
    user?.userSettings?.isCommercialNewsSubscriber ?? false;
  const visibleNews = news?.filter(
    (item) => isCommercialNewsSubscriber || item.type !== NewsType.Commercial,
  );
  const hasVisibleNews = !!visibleNews?.length;
  const isUnseenEventsLoaded = !!unseenEvents;

  const resetNews = useEffectEvent(async () => {
    await resetUnseenEvents([UnseenEventType.News]);
  });

  useEffect(() => {
    const firstNewsCard = newsGridRef.current?.firstElementChild;

    if (!firstNewsCard || !isUnseenEventsLoaded) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio < FULLY_VISIBLE_RATIO) {
          return;
        }

        observer.disconnect();
        resetNews();
      },
      { threshold: FULLY_VISIBLE_RATIO },
    );

    observer.observe(firstNewsCard);

    return () => observer.disconnect();
  }, [hasVisibleNews, isUnseenEventsLoaded]);

  return (
    <section
      id={Anchor.News}
      className="flex scroll-mt-4 flex-col gap-3 px-4 py-6 md:gap-4 md:px-6 md:pt-10"
    >
      <SectionTitle>{t('home.news.title')}</SectionTitle>
      {visibleNews?.length === 0 && (
        <div className="py-4">
          <EmptyState
            label={t('home.news.emptyTitle')}
            description={t('home.news.emptyBody')}
          />
        </div>
      )}
      {hasVisibleNews && (
        <div
          ref={newsGridRef}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
        >
          {visibleNews.map((item, index) => (
            <NewsCard
              key={`${item.lastModificationDate}-${index}`}
              item={item}
            />
          ))}
        </div>
      )}
    </section>
  );
};
